import React, { useState, useEffect } from "react";
import {
  Calendar,
  User,
  IndianRupee,
  MapPin,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Printer,
} from "lucide-react";
import api from "../api/axiosInstance";

const MonthlyAttendancePaymentTracker = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [viewData, setViewData] = useState({ attendance: [], payments: [] });
  const [calendarDays, setCalendarDays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("get_employees.php")
      .then((res) => setEmployees(res.data.employees || []));
  }, []);

  useEffect(() => {
    if (selectedEmpId && selectedMonth) {
      fetchMonthlyOverview();
    }
  }, [selectedEmpId, selectedMonth]);

  const fetchMonthlyOverview = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split("-").map(Number);
      const firstDay = new Date(year, month - 1, 1).getDay();
      const daysInMonth = new Date(year, month, 0).getDate();

      const blanks = Array(firstDay).fill(null);
      const days = Array.from(
        { length: daysInMonth },
        (_, i) => `${selectedMonth}-${(i + 1).toString().padStart(2, "0")}`,
      );
      setCalendarDays([...blanks, ...days]);

      // NEW API: Fetches both attendance and advance payments for that month
      const res = await api.get(
        `get_monthly_overview.php?emp_id=${selectedEmpId}&month=${selectedMonth}`,
      );
      setViewData(res.data || { attendance: [], payments: [] });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDayData = (date) => {
    const att = viewData.attendance.find((a) => a.date === date);
    const pay = viewData.payments.filter((p) => p.date === date);
    return { att, pay };
  };
  const printAuditReport = async () => {
    try {
      const response = await api.get(
        `/generate_attendance_audit_pdf.php?emp_id=${selectedEmpId}&month=${selectedMonth}`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Audit_${currentEmp.name}_${selectedMonth}.pdf`,
      );
      link.click();
    } catch (err) {
      alert("Failed to generate audit report.");
    }
  };
  const currentEmp = employees.find((e) => e.id == selectedEmpId);

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col md:row justify-between items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 italic uppercase tracking-tighter">
            History Tracker
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">
            Attendance & Advance Payment Audit
          </p>
        </div>

        <div className="flex gap-3">
          <select
            className="p-3 bg-white border border-slate-200 rounded-xl font-black text-[10px] uppercase outline-none shadow-sm"
            value={selectedEmpId}
            onChange={(e) => setSelectedEmpId(e.target.value)}
          >
            <option value="">Select Employee...</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <input
            type="month"
            className="p-3 bg-white border border-slate-200 rounded-xl font-black text-[10px] outline-none shadow-sm"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          <button
            onClick={printAuditReport}
            className="bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase shadow-lg flex items-center gap-2 hover:bg-indigo-600 transition-all"
          >
            <Printer size={16} /> Print Audit
          </button>
        </div>
      </div>

      {selectedEmpId ? (
        loading ? (
          <div className="h-96 flex items-center justify-center font-black text-slate-300 animate-pulse italic uppercase">
            Building Audit Trail...
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-3 bg-slate-50 p-6 rounded-[40px] border border-slate-100">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div
                key={d}
                className="text-center font-black text-[10px] uppercase text-slate-400 pb-4"
              >
                {d}
              </div>
            ))}

            {calendarDays.map((date, idx) => {
              if (!date)
                return (
                  <div
                    key={`b-${idx}`}
                    className="h-32 bg-slate-100/30 rounded-3xl"
                  ></div>
                );

              const { att, pay } = getDayData(date);
              const isSunday = new Date(date).getDay() === 0;
              const isAbsent = att?.status === "Absent" || (!att && !isSunday);

              return (
                <div
                  key={date}
                  className={`relative h-32 p-4 rounded-[32px] border transition-all ${
                    isAbsent
                      ? "bg-rose-50 border-rose-100 text-rose-600"
                      : "bg-white border-white shadow-sm"
                  } ${isSunday && !att ? "bg-slate-100/50 border-transparent text-slate-300" : ""}`}
                >
                  <div className="flex justify-between items-start">
                    <span className="text-lg font-black italic">
                      {date.split("-")[2]}
                    </span>
                    {att && !isAbsent && (
                      <CheckCircle2 size={14} className="text-emerald-500" />
                    )}
                    {isAbsent && (
                      <XCircle size={14} className="text-rose-400" />
                    )}
                  </div>

                  <div className="mt-2">
                    {att && (
                      <>
                        <p className="text-[9px] font-black uppercase truncate text-indigo-500">
                          {att.project_name || "General"}
                        </p>
                        <p className="text-[10px] font-bold">
                          {currentEmp?.pay_type === "Hourly"
                            ? `${att.multiplier} Hrs`
                            : `${att.multiplier} Shift`}
                        </p>
                      </>
                    )}
                  </div>

                  {/* Payment Markers */}
                  <div className="absolute bottom-3 right-3 flex -space-x-2">
                    {pay.map((p, pIdx) => (
                      <div
                        key={pIdx}
                        className="w-8 h-8 rounded-full bg-emerald-500 text-white border-2 border-white flex items-center justify-center shadow-lg transform hover:scale-110 transition-all cursor-help"
                        title={`Paid: ₹${p.amount}`}
                      >
                        <IndianRupee size={12} strokeWidth={3} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="h-96 border-4 border-dashed border-slate-100 rounded-[48px] flex flex-col items-center justify-center text-slate-200">
          <User size={64} className="mb-4 opacity-10" />
          <p className="font-black uppercase tracking-widest">
            Select an employee to view Audit
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyAttendancePaymentTracker;
